import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ActiveTickIcon from "../../src/assets/ActiveTickIcon.svg";
import AddIcon from "../../src/assets/AddIcon.svg";
import InctiveCrossIcon from "../../src/assets/InactiveCrossIcon.svg";
import {
  getAllGroups,
  getGroupDetails,
  removeGroup,
} from "../api/requests/groups";
import CommDataGrid from "../components/CommDataGrid";
import CommonDialog from "../components/common/CommonDialog";
import CreateOrEditGroup from "../components/CreateOrEditGroup";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { formatFullDateTime } from "../utils/utils";
import { sectionTitleSx } from "./UserAnalytics";
import { containedButtonWithIconSx } from "./Users";

export interface PaginationType {
  page: number;
  pageSize: number;
}

interface Group {
  id: number;
  groupId: number;
  membersCount: number;
  createdOn: string;
  groupEntityState: string;
  groupName: string;
}

interface UserType {
  userId: number;
  fullName: string;
}

interface GroupMemberType {
  userId: number;
  userName: string;
}

interface RankUserDetailsType {
  rankId: number;
  rankName: string;
  users: UserType[];
}

const GroupsPage: React.FC = () => {
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [createEditDialogOpen, setCreateEditDialogOpen] =
    useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editGroupId, setEditGroupId] = useState<number | null>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [viewMembersList, setViewMembersList] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<Group | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [isDialogTransitioning, setIsDialogTransitioning] =
    useState<boolean>(false);
  const [isLoadingEditData, setIsLoadingEditData] = useState<boolean>(false);

  // Memoize API calls
  const fetchGroupDetails = useCallback(
    async (groupId: number, isForView: boolean) => {
      setIsLoadingEditData(true);
      try {
        const response = await getGroupDetails(groupId);
        if (response?.data) {
          setGroupName(response.data.groupName);
          setSelectedUsers(
            response.data.members?.map(
              (user: GroupMemberType) => user.userId
            ) ?? []
          );
          if (isForView) {
            setViewMembersList(
              response.data.members?.map(
                (user: GroupMemberType) => user.userName
              ) ?? []
            );
          }
        }
      } catch (error) {
        console.error("Error fetching group details:", error);
      } finally {
        setIsLoadingEditData(false);
      }
    },
    []
  );

  const fetchAllGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllGroups({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        q: searchText.trim(),
      });
      if (response?.data?.groupInfos) {
        const mappedGroups = response.data.groupInfos.map((group: Group) => ({
          groupId: group.groupId,
          id: group.groupId,
          membersCount: group.membersCount,
          createdOn: formatFullDateTime(group.createdOn),
          groupEntityState: group.groupEntityState,
          groupName: group.groupName,
        }));
        const ids = mappedGroups.map((g: Group) => g.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error("Duplicate group IDs detected:", ids);
        }
        setGroups(mappedGroups);
        setTotalRowsCount(response.data.totalCount);
      } else {
        setGroups([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch groups:", error);
      setGroups([]);
      setTotalRowsCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: Group) => {
      setAnchorEl(event.currentTarget);
      setSelectedRow(row);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEdit = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (selectedRow) {
        setDialogMode("edit");
        setEditGroupId(selectedRow.id);
        setIsLoadingEditData(true);
        setCreateEditDialogOpen(true);
      }
      handleMenuClose();
    },
    [selectedRow, handleMenuClose]
  );

  const openDeleteDialog = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setDeleteDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const handleDelete = useCallback(async () => {
    try {
      const response = selectedRow && (await removeGroup(selectedRow.id));
      if (response && response.status === 200) {
        setSelectedRow(null);
        setDeleteDialogOpen(false);
        showAlert("Group deleted successfully", "success");
        await fetchAllGroups();
      } else {
        showAlert("Failed to delete group", "error");
      }
    } catch (error) {
      console.error("Error while deleting the group", error);
    }
  }, [selectedRow, showAlert, fetchAllGroups]);

  const handleViewMembers = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (selectedRow) {
        setEditGroupId(selectedRow.id);
        setViewDialogOpen(true);
      }
      handleMenuClose();
    },
    [selectedRow, handleMenuClose]
  );

  const closeViewMembersDialog = useCallback(() => {
    setIsDialogTransitioning(true);
    setCreateEditDialogOpen(false);
    setViewDialogOpen(false);
    setViewMembersList([]);
    setEditGroupId(null);
    setGroupName("");
    setSelectedUsers([]);
    setIsLoadingEditData(false);
    setTimeout(() => setIsDialogTransitioning(false), 0);
  }, []);

  const handleViewDialogClose = useCallback(
    (_event: {}, reason: string) => {
      if (reason !== "backdropClick") {
        closeViewMembersDialog();
      }
    },
    [closeViewMembersDialog]
  );

  const openCreateGroupDialog = useCallback(() => {
    setDialogMode("create");
    setEditGroupId(null);
    setGroupName("");
    setSelectedUsers([]);
    setCreateEditDialogOpen(true);
  }, []);

  // Preserve original handleSearch function
  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // Fetch group details when dialogs open
  useEffect(() => {
    if (editGroupId && viewDialogOpen) {
      fetchGroupDetails(editGroupId, true);
    } else if (editGroupId && createEditDialogOpen && dialogMode === "edit") {
      fetchGroupDetails(editGroupId, false);
    }
  }, [
    editGroupId,
    viewDialogOpen,
    createEditDialogOpen,
    dialogMode,
    fetchGroupDetails,
  ]);

  // Prevent CreateEditDialog rendering during ViewMembersDialog close
  useEffect(() => {
    if (isDialogTransitioning && createEditDialogOpen) {
      setCreateEditDialogOpen(false);
    }
  }, [isDialogTransitioning, createEditDialogOpen]);

  // Fetch groups on mount and when dependencies change
  useEffect(() => {
    fetchAllGroups();
  }, [fetchAllGroups]);

  const closeCreateOrEditDialog = () => {
    setCreateEditDialogOpen(false);
    fetchAllGroups();
  };

  // Memoize columns for performance
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "groupName",
        headerName: "Group Name",
        flex: 1,
        headerClassName: "custom-header",
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ display: "contents" }}>
            {value || "--"}
          </Typography>
        ),
      },
      {
        field: "membersCount",
        headerName: "Members",
        flex: 1,
        headerClassName: "custom-header",
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ display: "contents" }}>
              {value ?? 0} Members
            </Typography>
          </Box>
        ),
      },
      {
        field: "createdOn",
        headerName: "Created Date",
        flex: 1,
        headerClassName: "custom-header",
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ display: "contents" }}>
            {value || "--"}
          </Typography>
        ),
      },
      {
        field: "groupEntityState",
        headerName: "Status",
        flex: 1,
        headerClassName: "custom-header",
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              borderRadius: "15px",
            }}
          >
            <img
              src={value === "Active" ? ActiveTickIcon : InctiveCrossIcon}
              alt="status-icon"
              style={{ width: 20, height: 20, marginRight: 8 }}
            />
            <span
              style={{
                borderRadius: "15px",
                fontSize: "14px",
              }}
            >
              {value || ""}
            </span>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        headerClassName: "custom-header",
        renderCell: ({ row }) => (
          <>
            {row.groupEntityState !== "Deleted" && (
              <>
                <IconButton onClick={(event) => handleMenuOpen(event, row)}>
                  <MoreVertIcon sx={{ color: "#64748B" }} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedRow?.id === row.id}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={menuItemStyles}
                >
                  {user?.permissions?.some(
                    (p) => p.module === "Group" && p.actions.includes("Edit")
                  ) && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
                  {user?.permissions?.some(
                    (p) => p.module === "Group" && p.actions.includes("Delete")
                  ) && <MenuItem onClick={openDeleteDialog}>Delete</MenuItem>}
                  <MenuItem onClick={handleViewMembers}>View Members</MenuItem>
                </Menu>
              </>
            )}
          </>
        ),
      },
    ],
    [
      user,
      anchorEl,
      selectedRow,
      handleMenuOpen,
      handleMenuClose,
      handleEdit,
      openDeleteDialog,
      handleViewMembers,
    ]
  );

  // Memoize rows
  const memoizedRows = useMemo(() => groups, [groups]);

  // Memoize buttons component
  const buttonsComponent = useMemo(
    () => (
      <>
        {user?.permissions?.some(
          (p) => p.module === "Group" && p.actions.includes("Create")
        ) && (
          <Button
            variant="contained"
            onClick={openCreateGroupDialog}
            sx={containedButtonWithIconSx}
          >
            <img src={AddIcon} /> Create Group{" "}
          </Button>
        )}
      </>
    ),
    [user, openCreateGroupDialog]
  );

  return (
    <Box>
      <CommDataGrid
        title="Groups Management"
        description="View and manage all groups in the system"
        rows={memoizedRows}
        columns={columns}
        section="Groups"
        renderButtons={() => buttonsComponent}
        setPaginationModel={setPaginationModel}
        paginationModel={paginationModel}
        totalRowsCount={totalRowsCount}
        searchText={searchText}
        handleSearch={handleSearch}
        loading={loading}
      />
      {/* View Members Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        sx={dialogStyles}
        TransitionProps={{ timeout: 0 }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={sectionTitleSx}>View Group Members</Typography>
          <CloseIcon
            sx={{ fontSize: 16, color: "#9e9e9e", cursor: "pointer" }}
            onClick={closeViewMembersDialog}
          />
        </DialogTitle>
        <DialogContent>
          {viewMembersList.length > 0 ? (
            <List>
              {viewMembersList.map((member, index) => (
                <ListItem
                  key={index}
                  sx={{ "&.MuiListItem-root": { padding: 0 } }}
                >
                  <ListItemText
                    primary={member}
                    sx={{ whiteSpace: "wrap", fontSize: "12px" }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No Members found</Typography>
          )}
        </DialogContent>
      </Dialog>
      {/* Create/Edit Dialog - Conditionally Rendered */}
      {createEditDialogOpen && !isDialogTransitioning && !isLoadingEditData ? (
        <CreateOrEditGroup
          setCreateEditDialogOpen={closeCreateOrEditDialog}
          isEdit={dialogMode === "edit"}
          editGroupId={editGroupId!}
          groupSelectedUsers={selectedUsers}
          selectedGroupName={groupName}
        />
      ) : null}
      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <CommonDialog
          title="Delete Group"
          cancelButton={() => setDeleteDialogOpen(false)}
          actionButton={handleDelete}
          actionButtonTitle="Yes"
          cancelButtonTitle="Cancel"
        >
          <DialogContentText>
            <Typography
              fontSize="13px"
              sx={{ color: "#000000", paddingBottom: 1, pl: 2 }}
            >
              Are you sure you want to delete this group?
            </Typography>
            <Typography fontSize="12px" pl={2}>
              The group will be deleted if you click on 'Yes'.
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default GroupsPage;

const dialogStyles: SxProps<Theme> = {
  p: (theme) => theme.spacing(1, 2, 1, 2),
  ".MuiDialog-paper": {
    borderRadius: "4px",
    minWidth: 400,
  },
};

export const menuItemStyles: SxProps = {
  "& .MuiMenuItem-root:not(:last-child)": {
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  "& .MuiMenuItem-root": {
    padding: "8px 16px",
    fontSize: "14px",
  },
};
